<script>
	const Vue = require('vue');
	const page = require('page');
	const model = require('model/model');
	const utilities = require('utilities');
	const stringUtilities = require('utilities/stringUtilities');
	const {
		getStudentResearchAssessments,
		getStudentResearchAssessmentProduct,
		submitStudentResearchAssessment
	} = require('utilities/studentResearchAssessmentUtilities');
	const {
		fireEvent,
		events: { SRA_STUDENT_ANSWER_QUESTION, SRA_STEP_START, SRA_MODAL_OPENED, SRA_SUBMIT }
	} = require('utilities/analyticsUtilities');

	const NOT_ANSWERED = 'NOT ANSWERED';
	const TYPE_WRITTEN_ANSWER = 1;

	module.exports = {
		components: {
			'student-workspace-question': require('components/student/student-workspace-question.vue').default,
			modal: require('components/modal.vue').default,
			'load-mask': require('components/load-mask.vue').default,
			'text-to-speech-player-simple': require('components/text-to-speech-player-simple.vue').default
		},
		modelData: ['sraid', 'researchAssessments', 'activeResearchAssessment', 'userContext'],
		data() {
			return {
				assessment: null,
				product: null,
				unansweredQuestions: [],
				currentStepIndex: 0,
				answers: undefined,
				loading: false,
				submitted: false
			};
		},
		methods: {
			initializeAnswers() {
				this.answers = this.product.steps.reduce((answers, { questions, key }) => {
					if (questions) {
						answers[key] = questions.reduce((acc, question) => {
							acc[question.qid] = {
								aid: NOT_ANSWERED,
								qt: question.type
							};

							return acc;
						}, {});
					}

					return answers;
				}, {});
			},
			validate() {
				const { questions, key: stepKey } = this.currentStep;

				const hasQuestions = questions && questions.length > 0;
				const currentAnswers = this.answers[stepKey];

				if (hasQuestions && currentAnswers) {
					const unanswered = Object.entries(currentAnswers).reduce((acc, [qid, { aid, qt, t }], index) => {
						const isWritten = qt === TYPE_WRITTEN_ANSWER;
						const isAnswered = isWritten ? !!t : aid !== NOT_ANSWERED;

						if (!isAnswered) {
							acc.push({
								qid: Number(qid),
								number: index + 1
							});
						}
						return acc;
					}, []);

					this.unansweredQuestions = unanswered;
				}
			},
			updateModelEntry() {
				const { sraid } = this.assessment;
				const answers = this.answers;
				const currentStepIndex = this.currentStepIndex;

				this.activeResearchAssessment = {
					sraid,
					currentStepIndex,
					answers
				};
			},
			removeModelEntry() {
				model.remove('activeResearchAssessment');
			},
			answerHandler(answerData) {
				const {
					answer: { qid, aid }
				} = answerData;

				if (this.unansweredQuestions.length > 0) {
					this.unansweredQuestions = this.unansweredQuestions.filter(question => question.qid !== qid);
				}

				this.updateModelEntry();

				const { type, sraid } = this.assessment;
				fireEvent(SRA_STUDENT_ANSWER_QUESTION, {
					type,
					sraid,
					qid,
					aid
				});

				return Promise.resolve(answerData);
			},
			flattenAnswers() {
				return Object.values(this.answers).reduce((flattened, answers) => {
					for (let qid in answers) {
						const { aid, qt, t } = answers[qid];
						flattened[qid] = qt === TYPE_WRITTEN_ANSWER ? t : aid;
					}

					return flattened;
				}, {});
			},
			handleContinue() {
				this.validate();

				if (this.unansweredQuestions.length > 0 && this.currentStep.modal) {
					utilities.openModal('#research-assessment-step-confirmation');
					const { type, sraid } = this.assessment;
					fireEvent(SRA_MODAL_OPENED, {
						type,
						sraid,
						step: this.currentStep.key,
						unanswered: this.unansweredQuestionNumbers
					});
				} else {
					this.showNextStep();
				}
			},
			isUnanswered(questionId) {
				return !!this.unansweredQuestions.find(question => question.qid === questionId);
			},
			fireStepEvent() {
				const { type, sraid } = this.assessment;
				fireEvent(SRA_STEP_START, {
					type,
					sraid,
					step: this.currentStep.key
				});
			},
			handleSubmit() {
				this.loading = true;
				const flattenedAnswers = this.flattenAnswers();
				const { type, sraid } = this.assessment;

				submitStudentResearchAssessment(flattenedAnswers, sraid).then(() => {
					this.submitted = true;
					this.removeModelEntry();

					fireEvent(SRA_SUBMIT, {
						type,
						sraid
					});
					this.loading = false;
				});
			},
			showNextStep() {
				this.currentStepIndex++;
				this.unansweredQuestions = [];

				if (this.currentStepIndex === this.product.steps.length) {
					this.handleSubmit();
				} else {
					this.fireStepEvent();
					this.updateModelEntry();
				}

				Vue.nextTick(() => {
					window.scroll({
						top: 0,
						behavior: 'smooth'
					});
				});
			},
			modalConfirm() {
				utilities.closeModals();
				this.showNextStep();
			},
			getTextContent(html) {
				return stringUtilities.stripHTML(html);
			},
			closeModals() {
				utilities.closeModals();
			}
		},
		created() {
			this.loading = true;
			const sraid = this.sraid;
			const researchAssessments = this.researchAssessments;
			let assessmentPromise;

			if (!sraid) {
				page('/student');
			}

			if (researchAssessments) {
				assessmentPromise = Promise.resolve(researchAssessments);
			} else {
				assessmentPromise = getStudentResearchAssessments();
			}

			assessmentPromise.then(assessments => {
				const assessment = assessments.find(result => result.sraid === sraid);

				if (!assessment) {
					page('/student');
				}

				return getStudentResearchAssessmentProduct(assessment.type)
					.then(product => {
						this.product = product;
						this.assessment = assessment;

						const stored = this.activeResearchAssessment;
						if (stored && stored.sraid === assessment.sraid) {
							const { answers, currentStepIndex } = stored;

							this.answers = answers;
							this.currentStepIndex = currentStepIndex;
						} else {
							utilities.openModal('#confirm-sra-begin');
							this.initializeAnswers();
						}

						this.fireStepEvent();
						this.loading = false;
					})
					.catch(() => {
						page('/student');
					});
			});
		},
		computed: {
			unansweredQuestionNumbers() {
				return this.unansweredQuestions.map(({ number }) => number);
			},
			currentStep() {
				return this.product.steps[this.currentStepIndex];
			}
		}
	};
</script>
<style lang="scss">
	.student-research-assessment-workspace {
		.step-body {
			margin: 1rem 0;
		}

		.step-body-text {
			&,
			:last-child {
				display: inline;
			}
		}

		.step-video {
			width: 100%;
			height: auto;
			min-height: 20rem;
		}
	}
</style>
<template>
	<main class="main student-research-assessment-workspace">
		<load-mask v-if="loading"></load-mask>
		<template v-if="product">
			<header class="main-header">
				<div class="wrapper">
					<div v-if="product.backLink">
						<a
							class="main-header-back"
							:href="product.backLink.target"
							>{{ product.backLink.text }}</a
						>
					</div>
					<h1 class="main-header-title">
						{{ product.title }}
					</h1>
				</div>
			</header>
			<div
				class="main-content wrapper question-set"
				aria-live="polite">
				<div v-if="submitted">
					<h2
						class="submitted-heading"
						v-if="product.submitted.header">
						{{ product.submitted.header }}
					</h2>
					<p
						class="submitted-body"
						v-if="product.submitted.body">
						{{ product.submitted.body }}
					</p>
					<div
						v-if="product.submitted.backLink"
						class="text-centered">
						<a
							class="button submitted-back-link"
							:href="product.submitted.backLink.target">
							{{ product.submitted.backLink.text }}
						</a>
					</div>
				</div>
				<template v-else-if="currentStep">
					<h2
						class="step-title"
						v-if="currentStep.title">
						{{ currentStep.title }}
					</h2>
					<h3
						class="step-subtitle"
						v-if="currentStep.subtitle"
						v-html="currentStep.subtitle"></h3>
					<div
						class="step-body"
						v-if="currentStep.bodyText">
						<span
							class="step-body-text"
							v-html="currentStep.bodyText"></span>
						<text-to-speech-player-simple
							v-if="!product.hideTTS"
							:text="getTextContent(currentStep.bodyText)"
							:event-data="{
								tag: 'sra-instructions',
								loc: currentStep.key
							}">
						</text-to-speech-player-simple>
					</div>
					<div v-if="currentStep.video">
						<vimeo-player
							class="step-video"
							:video-id="currentStep.video.id"></vimeo-player>
					</div>
					<div
						class="step-questions question-set-questions"
						v-if="currentStep.questions">
						<div class="question-row">
							<student-workspace-question
								v-for="(question, index) in currentStep.questions"
								:key="question.qid"
								:q="question"
								:ws="{ productId: product.type }"
								:score-state.sync="answers[currentStep.key][question.qid]"
								:qnumb="index + 1"
								:aid="product.key"
								:qsid="currentStep.key"
								:answer-handler="answerHandler"
								:show-as-invalid="isUnanswered(question.qid)"
								:show-tts="!product.hideTTS"></student-workspace-question>
						</div>
					</div>
					<div class="text-centered">
						<button
							class="button step-action"
							@click="handleContinue">
							{{ currentStep.buttonText }}
						</button>
					</div>
				</template>
			</div>
			<modal
				v-if="currentStep && currentStep.modal"
				id="research-assessment-step-confirmation">
				<h2 slot="header-container">{{ currentStep.modal.title }}</h2>
				<div slot="content">
					<p v-if="currentStep.modal.bodyText">{{ currentStep.modal.bodyText }}</p>
					<p>The following questions were not answered:</p>
					<ul>
						<li
							v-for="unanswered in unansweredQuestionNumbers"
							:key="unanswered">
							{{ unanswered }}
						</li>
					</ul>
					<div class="text-centered button-container sra-modal-cancel">
						<button
							v-if="currentStep.modal.cancelText"
							class="button -outline"
							data-modal-close>
							{{ currentStep.modal.cancelText }}
						</button>
						<button
							v-if="currentStep.modal.continueText"
							class="button -outline sra-modal-action"
							@click="modalConfirm">
							{{ currentStep.modal.continueText }}
						</button>
					</div>
				</div>
			</modal>
		</template>
		<modal
			id="confirm-sra-begin"
			:no-auto-close="true"
			:closeable="false">
			<template slot="header-container">
				<h2>Ready to Start?</h2>
			</template>
			<div slot="content">
				<p>Once you start you will have to finish and submit.</p>
				<p>Ready to start?</p>
				<p class="text-centered">
					<a
						class="sra-begin-modal-cancel button -outline"
						href="/student">
						Not Yet
					</a>
					<button
						class="button sra-begin-modal-confirm"
						@click="closeModals">
						Yes
					</button>
				</p>
			</div>
		</modal>
	</main>
</template>
